/**
 * Secure Navigation - Role-based navigation menu system
 * Shows only relevant navigation items based on user role
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useRole } from '../../contexts/RoleContext';
import { useAuth } from '../../contexts/AuthContext';
import { ProtectedComponent } from '../common/ProtectedComponent';
import { getNavigationIcon } from './NavigationIcons';
import { useGalleryStatus } from '../../hooks/useGalleryStatus';

// Navigation configuration for different roles
const NAVIGATION_CONFIG = {
  // Public navigation (for unauthenticated users)
  PUBLIC: [
    { path: '/', label: 'Home', icon: 'home' },
    { path: '/programs', label: 'Programs', icon: 'programs' },
    { path: '/join-our-team', label: 'Join Us', icon: 'join-team' },
   // { path: '/our-team', label: 'Our Team', icon: 'users' },
    { path: '/about', label: 'Our Story', icon: 'book' }
  ],

  // Platform admin navigation (focused on platform management)
  PLATFORM_ADMIN: [
    { path: '/admin/platform', label: 'Platform Dashboard', icon: 'dashboard' }
  ],

  // Club admin navigation (focused on club operations)
  CLUB_ADMIN: [
    { path: '/admin/club', label: 'Club Dashboard', icon: 'dashboard' }
  ],

  // Branch admin navigation
  BRANCH_ADMIN: [
    { path: '/branch/:branch_id/dashboard', label: 'Branch Dashboard', icon: 'dashboard' }
  ],

  // Coach navigation (focused on training and evaluation)
  COACH: [
    { path: '/coach/dashboard', label: 'Dashboard', icon: 'dashboard' }
  ],

  // Parent navigation (focused on family and enrollment)
  PARENT: [
    { path: '/dashboard', label: 'Dashboard', icon: 'dashboard' }
  ],

  // Player navigation (focused on personal progress)
  PLAYER: [
    { path: '/dashboard', label: 'Dashboard', icon: 'dashboard' },
    { path: '/player/programs', label: 'My Programs', icon: 'programs' },
    { path: '/player/assessments', label: 'My Assessments', icon: 'growth' }
  ]
};

export const SecureNavigationItems = ({ layoutStyle = 'horizontal', onNavigate }) => {
  const [openDropdown, setOpenDropdown] = useState(null);

  const { userRole, isPlatformAdmin, isClubAdmin, isBranchAdmin, isCoach, isParent, isPlayer, isAuthenticated } = useRole();
  const { user } = useAuth(); // Need user object for resolving dynamic paths like branch_id
  const { isGalleryEnabled } = useGalleryStatus(); // Check if gallery is enabled

  // Debug logging for role-based menu rendering
  console.log('[SecureNavigationItems] isAuthenticated:', isAuthenticated, 'userRole:', userRole, 'isGalleryEnabled:', isGalleryEnabled);

  /**
   * Resolve dynamic path parameters
   * Replaces :param with actual value from user context
   */
  const resolvePath = (path) => {
    if (!path) return path;

    // Replace :branch_id with user.branch_id or user.location_id
    if (path.includes(':branch_id')) {
      const branch_id = user?.branch_id || user?.location_id || '';
      return path.replace(':branch_id', branch_id);
    }

    return path;
  };

  /**
   * Process navigation items to resolve dynamic paths
   */
  const processItems = (items) => {
    return items.map(item => {
      const processedItem = { ...item };

      // Resolve main path
      if (processedItem.path) {
        processedItem.path = resolvePath(processedItem.path);
      }

      // Process sub-items if they exist
      if (processedItem.items) {
        processedItem.items = processItems(processedItem.items);
      }

      return processedItem;
    });
  };

  // Get navigation items based on role
  const getNavigationItems = () => {
    let baseItems = [];

    if (!isAuthenticated) {
      baseItems = [...NAVIGATION_CONFIG.PUBLIC];
      // Add Gallery to public navigation if enabled
      if (isGalleryEnabled) {
        baseItems.push({ path: '/gallery', label: 'Gallery', icon: 'image' });
      }
      return processItems(baseItems);
    }

    if (isPlatformAdmin) {
      return processItems(NAVIGATION_CONFIG.PLATFORM_ADMIN);
    }

    if (isClubAdmin) {
      return processItems(NAVIGATION_CONFIG.CLUB_ADMIN);
    }

    if (isBranchAdmin) {
      return processItems(NAVIGATION_CONFIG.BRANCH_ADMIN);
    }

    if (isCoach) {
      return processItems(NAVIGATION_CONFIG.COACH);
    }

    if (isParent) {
      return processItems(NAVIGATION_CONFIG.PARENT);
    }

    if (isPlayer) {
      return processItems(NAVIGATION_CONFIG.PLAYER);
    }

    return [];
  };

  const navigationItems = getNavigationItems();

  const renderNavigationItem = (item, index) => {
    // Handle dropdown/submenu items
    if (item.items && item.items.length > 0) {
      const isOpen = openDropdown === index;

      const handleDropdownKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setOpenDropdown(isOpen ? null : index);
        } else if (e.key === 'Escape') {
          setOpenDropdown(null);
        }
      };

      return (
        <div key={index} className={`nav-dropdown nav-dropdown--${layoutStyle}`}>
          <button
            className={`nav-dropdown-trigger nav-dropdown-trigger--${layoutStyle}`}
            onClick={() => setOpenDropdown(isOpen ? null : index)}
            onKeyDown={handleDropdownKeyDown}
            aria-expanded={isOpen}
            aria-haspopup="true"
          >
            {item.icon && <span className="nav-icon">{getNavigationIcon(item.icon)}</span>}
            <span className="nav-label">{item.label}</span>
            <span className="nav-arrow" aria-hidden="true">▼</span>
          </button>
          {isOpen && (
            <div className={`nav-dropdown-menu nav-dropdown-menu--${layoutStyle}`} role="menu">
              {item.items.map((subItem, subIndex) => (
                <Link
                  key={subIndex}
                  to={subItem.path}
                  className={`nav-dropdown-item nav-dropdown-item--${layoutStyle}`}
                  role="menuitem"
                  onClick={() => {
                    setOpenDropdown(null);
                    onNavigate?.();
                  }}
                >
                  {subItem.icon && <span className="nav-icon">{getNavigationIcon(subItem.icon)}</span>}
                  <span className="nav-label">{subItem.label}</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      );
    }

    // Handle regular navigation items
    return (
      <Link
        key={index}
        to={item.path}
        className={`nav-item nav-item--${layoutStyle}`}
        onClick={onNavigate}
      >
        {item.icon && <span className="nav-icon">{getNavigationIcon(item.icon)}</span>}
        <span className="nav-label">{item.label}</span>
      </Link>
    );
  };

  return (
    <nav className={`secure-navigation secure-navigation--${layoutStyle}`}>
      {navigationItems.map(renderNavigationItem)}
    </nav>
  );
};

// Role-specific navigation components
export const PlatformAdminNav = ({ layoutStyle = 'horizontal' }) => (
  <ProtectedComponent role="platform_admin,club_admin,branch_admin">
    <SecureNavigationItems layoutStyle={layoutStyle} />
  </ProtectedComponent>
);

export const ClubAdminNav = ({ layoutStyle = 'horizontal' }) => (
  <ProtectedComponent role="club_admin">
    <SecureNavigationItems layoutStyle={layoutStyle} />
  </ProtectedComponent>
);

export const BranchAdminNav = ({ layoutStyle = 'horizontal' }) => (
  <ProtectedComponent role="branch_admin">
    <SecureNavigationItems layoutStyle={layoutStyle} />
  </ProtectedComponent>
);

export const CoachNav = ({ layoutStyle = 'horizontal' }) => (
  <ProtectedComponent role="coach">
    <SecureNavigationItems layoutStyle={layoutStyle} />
  </ProtectedComponent>
);

export const ParentNav = ({ layoutStyle = 'horizontal' }) => (
  <ProtectedComponent role="parent">
    <SecureNavigationItems layoutStyle={layoutStyle} />
  </ProtectedComponent>
);

export const PublicNav = ({ layoutStyle = 'horizontal' }) => (
  <ProtectedComponent requireAuth={false}>
    <SecureNavigationItems layoutStyle={layoutStyle} />
  </ProtectedComponent>
);

export default SecureNavigationItems;